import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import EditIcons from '@/icons/EditIcons'
import { updateServiceConfigoptionMetadata } from '@/modules/Service/services'
import { Check, Plus, Trash2, X } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FormItem, IconButton, Typogaphy } from 'vnetwork-ui'

interface MainServiceColocationProps {
    formControl: UseFormReturn<any>
}

export interface RackEntry {
    idc: string
    rack: string
    position: { start_u: number; end_u: number } | null
}

// Helper function to format U position display
export const formatUPosition = (startU?: number, endU?: number): string => {
    if (!startU && !endU) return ''
    if (startU === endU || !endU) return `[${startU}]`
    return `[${startU}-${endU}]`
}

// Helper function to parse U position from string "[1-2]" or "[2]"
export const parseUPosition = (
    value: string
): { start_u: number; end_u: number } | null => {
    if (!value) return null

    // Remove brackets and whitespace
    const cleaned = value.replace(/[\[\]\s]/g, '')

    // Check if it's a range format: "1-2"
    if (cleaned.includes('-')) {
        const [start, end] = cleaned.split('-').map(Number)
        if (!isNaN(start) && !isNaN(end)) {
            return { start_u: start, end_u: end }
        }
    }

    // Check if it's a single number format: "2"
    const single = Number(cleaned)
    if (!isNaN(single)) {
        return { start_u: single, end_u: single }
    }

    return null
}

// Helper to calculate total U from a rack entry
export const calculateRackU = (rack: RackEntry): number => {
    if (!rack.position) return 0
    return rack.position.end_u - rack.position.start_u + 1
}

// Helper to calculate total U from all racks
export const calculateTotalU = (racks: RackEntry[]): number => {
    return racks.reduce((sum, rack) => sum + calculateRackU(rack), 0)
}

const MainServiceColocation: React.FC<MainServiceColocationProps> = ({
    formControl,
}) => {
    const { t } = useTranslation()
    const [isEditing, setIsEditing] = useState(false)

    // Get the main service colocation U config
    const serviceConfig = formControl.watch('service')

    const currentMetadata = serviceConfig?.metadata || {}
    const existingRacks = currentMetadata.racks || []

    // Initialize racks state - support both old single rack format and new multiple racks format
    const initializeRacks = (): RackEntry[] => {
        // If new format exists, use it
        if (existingRacks.length > 0) {
            return existingRacks.map((r: any) => ({
                idc: r.idc || '',
                rack: r.rack || '',
                position: r.position || null,
            }))
        }

        // If old format exists, migrate it (from old name/idc_location format)
        const oldRack = currentMetadata.rack
        if (oldRack && (oldRack.name || oldRack.idc)) {
            return [
                {
                    idc: oldRack.idc_location || oldRack.idc || '',
                    rack: oldRack.name || oldRack.rack || '',
                    position: oldRack.position || null,
                },
            ]
        }

        // Default: empty rack
        return [
            {
                idc: '',
                rack: '',
                position: null,
            },
        ]
    }

    const [racks, setRacks] = useState<RackEntry[]>(initializeRacks())
    const [totalU, setTotalU] = useState(0)

    // Sync racks with form control
    useEffect(() => {
        formControl.setValue('racks', racks)
    }, [racks])

    // Watch form changes to update totalU in real-time
    useEffect(() => {
        if (!isEditing) {
            setTotalU(calculateTotalU(racks))
            return
        }

        const subscription = formControl.watch((value) => {
            const formRacks = value.racks || []
            const parsedRacks = formRacks.map((r: any) => ({
                idc: r?.idc || '',
                rack: r?.rack || '',
                position:
                    typeof r?.position === 'string'
                        ? parseUPosition(r.position)
                        : r?.position,
            }))
            const total = parsedRacks.reduce((sum: number, rack: RackEntry) => {
                if (!rack.position) return sum
                return sum + (rack.position.end_u - rack.position.start_u + 1)
            }, 0)
            setTotalU(total)
        })

        return () => subscription.unsubscribe()
    }, [formControl, isEditing, racks])

    const commitmentValue = serviceConfig?.commitment_value || 0
    const remainingU = commitmentValue - totalU

    const { mutateAsync: updateMetadata, isPending } = useMutationWithHandler(
        {
            mutationFn: () => {
                const newMetadata = {
                    ...currentMetadata,
                    racks: racks?.map((rack) => ({
                        idc: rack.idc,
                        rack: rack.rack,
                        position: rack.position,
                    })),
                }

                return updateServiceConfigoptionMetadata({
                    serviceID: serviceConfig.service_id,
                    service_configoption_id:
                        serviceConfig.service_configoption_id,
                    metadata: newMetadata,
                })
            },
        },
        {
            reactQueryKeys: [QUERY_KEYS.SERVICE, QUERY_KEYS.SERVICES],
        }
    )

    const handleEditClick = () => {
        setRacks(initializeRacks())
        setIsEditing(true)
    }

    const handleCancel = () => {
        setRacks(initializeRacks())
        setIsEditing(false)
    }

    const handleSave = async () => {
        // Get updated values from form
        const formRacks = formControl.getValues('racks') || []

        // Parse position strings to objects
        const parsedRacks = formRacks.map((r: any) => ({
            idc: r.idc || '',
            rack: r.rack || '',
            position:
                typeof r.position === 'string'
                    ? parseUPosition(r.position)
                    : r.position,
        }))

        // Calculate total U from parsed racks
        const totalUParsed = parsedRacks.reduce(
            (sum: number, rack: RackEntry) => {
                if (!rack.position) return sum
                return sum + (rack.position.end_u - rack.position.start_u + 1)
            },
            0
        )

        // Validate total U matches commitment value
        if (totalUParsed !== commitmentValue) {
            alert(
                t('total_u_must_equal_commitment_value', {
                    totalU: totalUParsed,
                    commitmentValue: commitmentValue,
                })
            )
            return
        }

        // Validate all racks have required fields
        const hasInvalidRack = parsedRacks.some(
            (rack: RackEntry) => !rack.idc || !rack.rack || !rack.position
        )
        if (hasInvalidRack) {
            alert(t('all_racks_must_have_required_fields'))
            return
        }

        // Update racks state with parsed values
        setRacks(parsedRacks)

        try {
            // Wait for state to update, then save
            await new Promise((resolve) => setTimeout(resolve, 100))
            await updateMetadata()
            setIsEditing(false)
        } catch (error) {
            console.error('Failed to update metadata:', error)
        }
    }

    const handleAddRack = () => {
        const newRacks = [
            ...racks,
            {
                idc: '',
                rack: '',
                position: { start_u: 1, end_u: 1 },
            },
        ]
        setRacks(newRacks)
        formControl.setValue('racks', newRacks)
    }

    const handleRemoveRack = (index: number) => {
        if (racks.length === 1) {
            alert(t('must_have_at_least_one_rack'))
            return
        }
        const newRacks = racks.filter((_, i) => i !== index)
        setRacks(newRacks)
        formControl.setValue('racks', newRacks)
    }

    if (!serviceConfig) return null

    return (
        <div className="p-4 rounded-[4px] border bg-muted/50 space-y-2">
            {/* Header */}
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                    <Typogaphy className="text-sm font-medium text-foreground">
                        {t('provider-colocation-information-main-service')}
                    </Typogaphy>
                    <div className="text-xs text-muted-foreground">
                        {t('total')}:{' '}
                        <span
                            className={`font-semibold ${totalU === commitmentValue ? 'text-green-600' : 'text-red-600'}`}
                        >
                            {totalU}
                        </span>{' '}
                        / {commitmentValue} U
                    </div>
                </div>
                {!isEditing && (
                    <IconButton
                        size="small"
                        onClick={handleEditClick}
                        className="hover:text-blue-600 dark:hover:text-blue-400 hover:scale-110 transition-all"
                    >
                        <EditIcons />
                    </IconButton>
                )}
            </div>

            {/* View Mode */}
            {!isEditing && (
                <div className="space-y-2">
                    {racks?.map((rack, index) => (
                        <div
                            key={index}
                            className="p-3 border rounded bg-background/50"
                        >
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-medium text-muted-foreground">
                                    {t('rack')} #{index + 1}
                                </span>
                                <span className="text-xs font-semibold text-blue-600 dark:text-blue-400">
                                    {calculateRackU(rack)} U
                                </span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                                <div>
                                    <Label className="text-xs text-muted-foreground">
                                        {t('place_of_idc')}
                                    </Label>
                                    <p className="font-medium">
                                        {rack.idc || t('not_set')}
                                    </p>
                                </div>
                                <div>
                                    <Label className="text-xs text-muted-foreground">
                                        {t('place_of_rack')}
                                    </Label>
                                    <p className="font-medium">
                                        {rack.rack || t('not_set')}
                                    </p>
                                </div>
                                <div>
                                    <Label className="text-xs text-muted-foreground">
                                        {t('place_of_u')}
                                    </Label>
                                    <p className="font-medium">
                                        {rack.position
                                            ? formatUPosition(
                                                  rack.position.start_u,
                                                  rack.position.end_u
                                              )
                                            : t('not_set')}
                                    </p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Edit Mode */}
            {isEditing && (
                <div className="space-y-2">
                    {/* Racks list */}
                    {racks?.map((rack, index) => (
                        <div
                            key={index}
                            className="p-4 border rounded bg-background/50 space-y-2"
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <span className="text-xs font-medium text-muted-foreground">
                                        {t('rack')} #{index + 1}
                                    </span>
                                    <span className="text-xs font-semibold text-blue-600 dark:text-blue-400">
                                        {calculateRackU(rack)} U
                                    </span>
                                </div>
                                {racks.length > 1 && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleRemoveRack(index)}
                                        className="h-7 w-7 p-0 hover:bg-red-50 hover:text-red-600"
                                    >
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <FormItem
                                    type="textfield"
                                    control={formControl.control}
                                    name={`racks.${index}.idc`}
                                    label={t('place_of_idc')}
                                    placeholder={t('place_of_idc')}
                                    defaultValue={rack.idc}
                                />
                                <FormItem
                                    type="textfield"
                                    control={formControl.control}
                                    name={`racks.${index}.rack`}
                                    label={t('place_of_rack')}
                                    placeholder={t('place_of_rack')}
                                    defaultValue={rack.rack}
                                />
                                <FormItem
                                    type="textfield"
                                    control={formControl.control}
                                    name={`racks.${index}.position`}
                                    label={t('place_of_u')}
                                    placeholder={t('place_of_u_placeholder')}
                                    defaultValue={
                                        rack.position
                                            ? formatUPosition(
                                                  rack.position.start_u,
                                                  rack.position.end_u
                                              )
                                            : ''
                                    }
                                />
                            </div>
                        </div>
                    ))}
                    <div className="flex justify-end gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleAddRack}
                            disabled={
                                isPending ||
                                totalU === commitmentValue ||
                                remainingU <= 0 ||
                                racks.length >= commitmentValue
                            }
                        >
                            <Plus className="h-4 w-4 mr-1" />
                            {t('add-rack')}
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleCancel}
                            disabled={isPending}
                        >
                            <X className="h-3.5 w-3.5 mr-1" />
                            {t('cancel')}
                        </Button>
                        <Button
                            size="sm"
                            onClick={handleSave}
                            disabled={isPending || totalU !== commitmentValue}
                        >
                            <Check className="h-3.5 w-3.5 mr-1" />
                            {isPending ? t('saving') : t('save')}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    )
}

export default MainServiceColocation
